import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';

interface RegisterProblemModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (description: string, problemType: string, impact: string) => void;
  isLoading?: boolean;
}

const PROBLEM_TYPES = [
  { value: 'operacional', label: 'Operacional' },
  { value: 'qualidade', label: 'Qualidade' },
  { value: 'seguranca', label: 'Segurança' },
  { value: 'equipamento', label: 'Equipamento' },
  { value: 'material', label: 'Material' },
  { value: 'outro', label: 'Outro' },
];

const IMPACT_LEVELS = [
  { value: 'baixo', label: 'Baixo' },
  { value: 'medio', label: 'Médio' },
  { value: 'alto', label: 'Alto' },
  { value: 'critico', label: 'Crítico' },
];

export const RegisterProblemModal: React.FC<RegisterProblemModalProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
}) => {
  const [description, setDescription] = useState('');
  const [problemType, setProblemType] = useState('operacional');
  const [impact, setImpact] = useState('medio');

  if (!open) return null;

  const canConfirm = description.trim().length >= 10;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(description.trim(), problemType, impact);
  };

  const handleClose = () => {
    setDescription('');
    setProblemType('operacional');
    setImpact('medio');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-zinc-900 rounded shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-red-500" />
            <span className="font-semibold text-sm text-zinc-800 dark:text-zinc-100">
              Registrar Problema
            </span>
          </div>
          <button onClick={handleClose} className="text-zinc-400 hover:text-zinc-600">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4 space-y-4">
          {/* Problem Type */}
          <div>
            <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
              Tipo do problema
            </label>
            <select
              value={problemType}
              onChange={(e) => setProblemType(e.target.value)}
              className="w-full text-sm border border-zinc-300 dark:border-zinc-600 rounded px-3 py-1.5 bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200"
            >
              {PROBLEM_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Impact */}
          <div>
            <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
              Impacto
            </label>
            <select
              value={impact}
              onChange={(e) => setImpact(e.target.value)}
              className="w-full text-sm border border-zinc-300 dark:border-zinc-600 rounded px-3 py-1.5 bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200"
            >
              {IMPACT_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-1">
              Descrição do problema (mín. 10 caracteres)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descreva o problema encontrado..."
              rows={4}
              className="w-full text-sm border border-zinc-300 dark:border-zinc-600 rounded px-3 py-2 bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-zinc-200 dark:border-zinc-700">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="px-3 py-1.5 text-xs rounded border border-zinc-300 dark:border-zinc-600 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm || isLoading}
            className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Registrando...' : 'Registrar Problema'}
          </button>
        </div>
      </div>
    </div>
  );
};
